import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Loader } from '@frame/ui-components/unified-search/loader'
import { Results } from '@frame/ui-components/unified-search/results'
import { SearchFooter } from '@frame/ui-components/unified-search/search-footer'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { H2 } from '@frame/ui-primitives/h2'
import { Input } from '@frame/ui-primitives/input'

import { EventScope, InternalEventType } from '@frame/lib/api/events/types'
import { buildSearchViewProps } from '@frame/lib/api/events/utils'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { Item, ItemKind, useGetItems } from '@frame/lib/hooks/useGetItems'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useSelectItem } from '@frame/lib/hooks/useSelectItem'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { isSkippable } from '@frame/lib/utils/tasks'
import { DataAttribute } from '@frame/types'
import { useDebounceCallback } from '@react-hook/debounce'

import { SkipEmployer } from './skip-employer'

const CONTAINER_ID = 'search-employers'
const LIMIT_MAX = 4

export function SearchEmployer() {
  const navigate = useNavigate()
  const location = useLocation()
  const task = useDirectTask()
  const showExitPrompt = useShowExitPrompt()
  const { selectItem, isLoading: isLoadingCredentials } = useSelectItem()
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { handleEvent } = useEventHandler()
  const title = useHeaderTitle()
  const [isMounted, setIsMounted] = useState(false)
  const oneSecond = 1000
  const debouncedHandleEvent = useDebounceCallback(handleEvent, oneSecond)

  const { items, isLoading, isValidating } = useGetItems({
    query: searchQuery,
    kinds: [ItemKind.EMPLOYER, ItemKind.GIG],
    limit: LIMIT_MAX,
  })
  const params = new URLSearchParams(location.search)

  useEffect(() => {
    const paramsOnMount = new URLSearchParams(location.search)
    const query: string = paramsOnMount.get('q') || ''
    if (!isMounted) {
      handleEvent(InternalEventType.EMPLOYER_SEARCH_VIEWED, buildSearchViewProps(EventScope.EMPLOYER, task, query))
    }
    setIsMounted(true)
  }, [task, location.search, handleEvent, isMounted])

  useEffect(() => {
    const paramsOnMount = new URLSearchParams(location.search)
    const query = paramsOnMount.get('q')

    if (query) {
      setSearchQuery(query)
    }
  }, [location.search])

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target

    params.set('q', value)
    setSearchQuery(value)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true, state: location.state })
    debouncedHandleEvent(InternalEventType.EMPLOYER_QUERIED, buildSearchViewProps(EventScope.EMPLOYER, task, value))
  }

  async function handleSelectItem({ item }: { item: Item }) {
    setSearchQuery(item?.name)
    params.set('q', item?.name)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true, state: location.state })
    selectItem({ item, employerName: item.name })
  }

  const showLoader = searchQuery && (isLoading || isValidating || isLoadingCredentials)
  const showBackButton = !location.state?.fromConsent
  const showSkipButton = task && isSkippable(task)

  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        disabled={isLoadingCredentials}
        showExitPrompt={showExitPrompt}
        exitPromptTitle="Exit Employer Search"
        exitPromptType={ExitPromptType.EMPLOYER}
        backButton={{ visible: showBackButton }}
      />
      <Body
        id={CONTAINER_ID}
        data-cy={DataAttribute.SCREEN_UNIFIED_SEARCH}
        className="relative h-full p-5"
        paddingTopOverride={8}
      >
        <H2 className="text mb-5 text-left text-moss-950">Find your employer</H2>
        <Input
          data-dd-privacy="allow"
          ref={inputRef}
          data-cy={DataAttribute.UNIFIED_SEARCH_INPUT}
          autoFocus
          disabled={isLoadingCredentials}
          tagType={null}
          placeholder="e.g. Walmart"
          label="Employer"
          value={searchQuery}
          onChange={handleInputChange}
          className="mb-3"
        />
        {showSkipButton && <SkipEmployer task={task} />}
        {showLoader ? (
          <Loader />
        ) : (
          <Results query={searchQuery} items={items} kind={ItemKind.EMPLOYER} handleSelectItem={handleSelectItem} />
        )}
      </Body>
      <SearchFooter kind={ItemKind.EMPLOYER} />
    </>
  )
}
